/**
 * AdvisorBilling — SB-Pricing (Juni 2026), Abrechnung per Rechnung (ohne Stripe).
 * Route: /advisor/abrechnung
 * Tarife: Starter 0 € (5 Mandanten) / Growth 39 € (25) / Pro 99 € (unbegrenzt)
 * + Mandantenlizenz 16 €/Mandant/Monat — jeder angefangene Monat zählt voll.
 *
 * Self-Service-Tarifwechsel (SB-Vertrag v1.0): SB wählt Tarif, stimmt dem
 * Vertrag (/steuerberater-agb) zu → sofort aktiv (Upgrade) bzw. zum
 * Monatsende (Downgrade). Rechnungen: Zahlungsziel 10 Tage nach Erhalt.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft, Loader2, Check, Crown, Users, Download, Info,
  BadgeCheck, Sparkles, FileText, UserCheck, Clock, ArrowDownCircle, ScrollText, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = { fontFamily: 'Manrope, sans-serif' };

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('advisor_token')}` });
const fmt = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n ?? 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '—');

const TIER_ORDER = ['starter', 'growth', 'pro'];
const TIER_FEATURES = {
  starter: ['Bis zu 5 aktive Mandanten', 'Voller Review-Workflow', 'DATEV/BMD-Exporte'],
  growth: ['Bis zu 25 aktive Mandanten', 'Alles aus Starter', 'Prioritäts-Support'],
  pro: ['Unbegrenzte Mandanten', 'Alles aus Growth', 'Persönlicher Ansprechpartner'],
};

function TierCard({ tierKey, cfg, current, pendingTier, onSelect }) {
  const isCurrent = current === tierKey;
  const isUpgrade = TIER_ORDER.indexOf(tierKey) > TIER_ORDER.indexOf(current);
  const isPending = pendingTier === tierKey;
  return (
    <Card className={`relative rounded-2xl border ${isCurrent ? 'border-emerald-300 ring-2 ring-emerald-200' : 'border-slate-100'} shadow-sm`} data-testid={`tier-card-${tierKey}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900" style={H}>{cfg.label}</h3>
          {isCurrent && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-medium text-emerald-700" data-testid={`tier-current-badge-${tierKey}`}>
              <BadgeCheck className="h-3 w-3" /> Aktuell
            </span>
          )}
          {isPending && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-testid={`tier-pending-badge-${tierKey}`}>
              <Clock className="h-3 w-3" /> Vorgemerkt
            </span>
          )}
        </div>
        <p className="mt-3">
          <span className="font-mono text-3xl font-semibold text-slate-900">{cfg.price === 0 ? '0 €' : `${cfg.price} €`}</span>
          <span className="text-xs text-slate-500"> /Monat</span>
        </p>
        <ul className="mt-4 space-y-2">
          {(TIER_FEATURES[tierKey] || []).map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
              <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        {!isCurrent && !isPending && (isUpgrade ? (
          <Button
            onClick={() => onSelect(tierKey, true)}
            className="mt-5 w-full bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid={`tier-upgrade-btn-${tierKey}`}
          >
            <Crown className="mr-2 h-4 w-4" /> Jetzt upgraden
          </Button>
        ) : (
          <Button
            onClick={() => onSelect(tierKey, false)}
            variant="outline"
            className="mt-5 w-full border-slate-200 text-slate-600 hover:bg-slate-50"
            data-testid={`tier-downgrade-btn-${tierKey}`}
          >
            <ArrowDownCircle className="mr-2 h-4 w-4" /> Downgrade zum Monatsende
          </Button>
        ))}
      </CardContent>
    </Card>
  );
}

/** Vertrags-Zustimmung beim Tarifwechsel (SB-Vertrag v1.0). */
function TierChangeDialog({ dialog, onClose, onConfirm, busy }) {
  const [accepted, setAccepted] = useState(false);
  useEffect(() => { setAccepted(false); }, [dialog]);
  if (!dialog) return null;
  const { tierKey, cfg, isUpgrade, currentLabel } = dialog;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" onClick={onClose} data-testid="tier-change-dialog-overlay">
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="tier-change-dialog">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold tracking-tight text-slate-900" style={H}>
              {isUpgrade ? 'Upgrade bestätigen' : 'Downgrade bestätigen'}
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="tier-dialog-close-btn"><X className="h-4 w-4" /></button>
          </div>
          <p className="mt-2 text-sm text-slate-600">
            Tarifwechsel von <strong>{currentLabel}</strong> zu <strong>{cfg.label}</strong>{' '}
            ({cfg.price === 0 ? '0 €' : `${cfg.price} €`}/Monat).
          </p>
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 space-y-1.5">
            {isUpgrade ? (
              <p><strong>Sofort wirksam:</strong> Der laufende Monat wird komplett im neuen Tarif abgerechnet.</p>
            ) : (
              <p><strong>Zum Monatsende:</strong> Der neue Tarif gilt ab dem 1. des Folgemonats — bis dahin läuft dein aktueller Tarif weiter.</p>
            )}
            <p>Abrechnung per Rechnung am Periodenende · Zahlungsziel 10 Tage nach Erhalt.</p>
          </div>
          <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="tier-terms-label">
            <Checkbox
              checked={accepted}
              onCheckedChange={(v) => setAccepted(!!v)}
              className="mt-0.5"
              data-testid="tier-terms-checkbox"
            />
            <span className="text-xs text-slate-600">
              Ich habe den{' '}
              <a href="/steuerberater-agb" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline hover:text-emerald-700" data-testid="tier-terms-link">
                ivoliq SB-Vertrag
              </a>{' '}
              und den{' '}
              <a href="/steuerberater-avv" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline hover:text-emerald-700" data-testid="tier-avv-link">
                SB-AVV
              </a>{' '}
              gelesen und stimme beiden zu.
            </span>
          </label>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} data-testid="tier-dialog-cancel-btn">Abbrechen</Button>
            <Button
              onClick={() => onConfirm(tierKey)}
              disabled={!accepted || busy}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="tier-dialog-confirm-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScrollText className="mr-2 h-4 w-4" />}
              {isUpgrade ? 'Upgrade aktivieren' : 'Downgrade vormerken'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** Kostenübernahme-Zustimmung bei Lizenz-Übernahme (16 €/Monat, SB-Vertrag v1.0). */
function TakeoverConsentDialog({ client, onClose, onConfirm, busy }) {
  const [accepted, setAccepted] = useState(false);
  useEffect(() => { setAccepted(false); }, [client]);
  if (!client) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" onClick={onClose} data-testid="takeover-dialog-overlay">
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="takeover-dialog">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold tracking-tight text-slate-900" style={H}>Kanzlei-Lizenz übernehmen</h3>
          <p className="mt-2 text-sm text-slate-600">
            Du übernimmst die ivoliq Pro-Lizenz für <strong>{client.name}</strong>.
          </p>
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 space-y-1.5">
            <p><strong>Kosten:</strong> 16 € / Monat — jeder angefangene Monat zählt voll.</p>
            <p>Bei laufendem Abo des Mandanten greift die Übernahme automatisch zum Abo-Ende.</p>
          </div>
          <label className="mt-4 flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={accepted}
              onCheckedChange={(v) => setAccepted(!!v)}
              className="mt-0.5"
              data-testid="takeover-consent-checkbox"
            />
            <span className="text-xs text-slate-600">
              Ich übernehme die Kosten der Pro-Lizenz für diesen Mandanten gemäß{' '}
              <a href="/steuerberater-agb" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline hover:text-emerald-700">
                ivoliq SB-Vertrag
              </a>.
            </span>
          </label>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} data-testid="takeover-dialog-cancel-btn">Abbrechen</Button>
            <Button
              onClick={() => onConfirm(client.client_id)}
              disabled={!accepted || busy}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="takeover-dialog-confirm-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserCheck className="mr-2 h-4 w-4" />}
              Lizenz übernehmen
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const INVOICE_STATUS = {
  sent: { label: 'Offen', cls: 'border-blue-200 bg-blue-50 text-blue-700' },
  paid: { label: 'Bezahlt', cls: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
};

export default function AdvisorBilling() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierDialog, setTierDialog] = useState(null); // {tierKey, cfg, isUpgrade, currentLabel}
  const [takeoverClient, setTakeoverClient] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [b, i] = await Promise.all([
        axios.get(`${API}/advisor/billing`, { headers: getHeaders() }),
        axios.get(`${API}/advisor/billing/invoices`, { headers: getHeaders() }),
      ]);
      setBilling(b.data);
      setInvoices(i.data.invoices);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/steuerberater'); return; }
      toast.error('Abrechnungsdaten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleTierSelect = (tierKey, isUpgrade) => {
    setTierDialog({
      tierKey,
      cfg: billing.tiers[tierKey],
      isUpgrade,
      currentLabel: billing.tier_label,
    });
  };

  const confirmTierChange = async (tierKey) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/advisor/billing/change-tier`,
        { tier: tierKey, accept_terms: true },
        { headers: getHeaders() }
      );
      toast.success(res.data.message);
      setTierDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tarifwechsel fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const cancelPendingTierChange = async () => {
    try {
      await axios.post(`${API}/advisor/billing/change-tier/cancel`, {}, { headers: getHeaders() });
      toast.success('Vorgemerkter Tarifwechsel zurückgezogen.');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler.');
    }
  };

  const confirmTakeover = async (clientId) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/advisor/clients/${clientId}/license-takeover`,
        { accept_license_costs: true },
        { headers: getHeaders() }
      );
      toast.success(res.data.message);
      setTakeoverClient(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Übernahme fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancelTakeover = async (clientId) => {
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/license-takeover/cancel`, {}, { headers: getHeaders() });
      toast.success('Vormerkung zurückgezogen.');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler.');
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await axios.get(`${API}/advisor/billing/invoices/${inv.id}/pdf`, { headers: getHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF konnte nicht geladen werden.'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (!billing) return null;

  const limitPct = billing.client_limit
    ? Math.min(100, Math.round((billing.active_clients / billing.client_limit) * 100))
    : null;

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }} data-testid="advisor-billing-page">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&family=Manrope:wght@400;500;600;700&display=swap" />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/advisor/dashboard')}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
          data-testid="billing-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zum Dashboard
        </button>

        <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={H}>Abrechnung</h1>
        <p className="mt-1 text-sm text-slate-500">Dein Kanzlei-Tarif, Mandantenlizenzen und Rechnungen im Überblick.</p>

        {billing.is_comp && (
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3" data-testid="comp-banner">
            <Sparkles className="h-4 w-4 text-violet-600 mt-0.5 shrink-0" />
            <p className="text-xs text-violet-900">
              <strong>Kostenlos freigeschaltet:</strong> Dein Tarif „{billing.tier_label}" wurde dir
              von ivoliq kostenlos zur Verfügung gestellt — inklusive aller Mandantenlizenzen.
            </p>
          </div>
        )}

        {/* Aktueller Status */}
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <Card className="rounded-2xl border border-slate-100 shadow-sm" data-testid="billing-current-tier-card">
            <CardContent className="p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Aktueller Tarif</p>
              <p className="mt-1.5 font-mono text-2xl font-semibold text-slate-900">{billing.tier_label}</p>
              <p className="mt-1 text-xs text-slate-500">{billing.is_comp ? 'Kostenlos (ivoliq)' : billing.tier_price === 0 ? 'Kostenlos' : `${fmt(billing.tier_price)} /Monat`}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-slate-100 shadow-sm" data-testid="billing-usage-card">
            <CardContent className="p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Aktive Mandanten</p>
              <p className="mt-1.5 font-mono text-2xl font-semibold text-slate-900">
                {billing.active_clients}{billing.client_limit ? ` / ${billing.client_limit}` : ''}
              </p>
              {limitPct !== null ? (
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${limitPct >= 100 ? 'bg-rose-500' : limitPct >= 80 ? 'bg-amber-500' : 'bg-sky-500'}`}
                    style={{ width: `${limitPct}%` }}
                  />
                </div>
              ) : (
                <p className="mt-1 text-xs text-slate-500">Unbegrenzt</p>
              )}
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-slate-100 shadow-sm" data-testid="billing-monthly-total-card">
            <CardContent className="p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Monatliche Kosten</p>
              <p className="mt-1.5 font-mono text-2xl font-semibold text-slate-900">{fmt(billing.monthly_total)}</p>
              <p className="mt-1 text-xs text-slate-500">
                Tarif {fmt(billing.tier_price)} + {billing.license_count} × {fmt(billing.license_price)} Lizenz
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-5 flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="billing-info-banner">
          <Info className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
          <p className="text-xs text-slate-600">
            Die Abrechnung erfolgt <strong>per Rechnung</strong> (kein Kartenzwang) am Ende der
            vereinbarten Periode — per E-Mail von <strong>billing@ivoliq.io</strong>, zahlbar per
            Überweisung mit <strong>Zahlungsziel 10 Tage</strong>. Mandantenlizenzen werden je
            <strong> angefangenem Monat voll</strong> berechnet. Es gilt der{' '}
            <a href="/steuerberater-agb" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline hover:text-emerald-700" data-testid="billing-terms-link">
              ivoliq SB-Vertrag
            </a>.
          </p>
        </div>

        {/* Vorgemerkter Downgrade */}
        {billing.pending_tier_change && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="pending-tier-change-banner">
            <div className="flex items-start gap-3">
              <Clock className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-900">
                <strong>Downgrade vorgemerkt:</strong> Tarif „{billing.pending_tier_change.tier_label || billing.pending_tier_change.tier}"
                gilt ab {fmtDate(billing.pending_tier_change.effective_at)}. Bis dahin läuft dein aktueller Tarif weiter.
              </p>
            </div>
            <button
              onClick={cancelPendingTierChange}
              className="shrink-0 text-xs text-amber-700 hover:text-rose-600 hover:underline"
              data-testid="cancel-tier-change-btn"
            >
              Zurückziehen
            </button>
          </div>
        )}

        {/* Tarife (bei Komp ausgeblendet) */}
        {!billing.is_comp && (
          <>
            <h2 className="mt-10 text-lg font-semibold text-slate-900" style={H}>Tarife</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Upgrade jederzeit (laufender Monat komplett im neuen Tarif) · Downgrade zum Monatsende.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {TIER_ORDER.map((key) => (
                <TierCard
                  key={key}
                  tierKey={key}
                  cfg={billing.tiers[key]}
                  current={billing.tier}
                  pendingTier={billing.pending_tier_change?.tier}
                  onSelect={handleTierSelect}
                />
              ))}
            </div>
          </>
        )}

        {/* Rechnungen */}
        <h2 className="mt-10 text-lg font-semibold text-slate-900" style={H}>Deine Rechnungen</h2>
        <Card className="mt-4 rounded-2xl border border-slate-100 shadow-sm" data-testid="advisor-invoices-card">
          <CardContent className="p-0">
            {invoices.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <FileText className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">Noch keine Rechnungen vorhanden.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-400">
                    <th className="px-5 py-3 font-medium">Rechnung</th>
                    <th className="px-5 py-3 font-medium">Zeitraum</th>
                    <th className="px-5 py-3 font-medium text-right">Betrag</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-50 last:border-0" data-testid={`advisor-invoice-row-${inv.invoice_number}`}>
                      <td className="px-5 py-3 font-mono text-xs">{inv.invoice_number}</td>
                      <td className="px-5 py-3 text-xs text-slate-500">{inv.period_label}</td>
                      <td className="px-5 py-3 text-right font-mono">{fmt(inv.total)}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${(INVOICE_STATUS[inv.status] || {}).cls}`}>
                          {(INVOICE_STATUS[inv.status] || {}).label}{inv.status === 'sent' && inv.days_overdue > 0 ? ` · ${inv.days_overdue} T. überfällig` : ''}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => downloadPdf(inv)} className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline" data-testid={`advisor-invoice-pdf-${inv.invoice_number}`}>
                          <Download className="h-3 w-3" /> PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Mandantenlizenzen */}
        <h2 className="mt-10 text-lg font-semibold text-slate-900" style={H}>Mandantenlizenzen</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          {fmt(billing.license_price)}/Monat pro Mandant — deine Mandanten erhalten vollen Pro-Zugang ohne eigene Zahlung.
        </p>
        <Card className="mt-4 rounded-2xl border border-slate-100 shadow-sm" data-testid="licensed-clients-card">
          <CardContent className="p-0">
            {billing.licensed_clients.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <Users className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">Noch keine Kanzlei-Lizenzen vergeben.</p>
                <p className="mt-1 text-xs text-slate-400">
                  Nutze „Mandant anlegen" im Dashboard — neue Mandanten laufen automatisch über die Kanzlei-Lizenz.
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {billing.licensed_clients.map((c) => (
                    <tr key={c.client_id} className="border-b border-slate-50 last:border-0" data-testid={`licensed-client-row-${c.client_id}`}>
                      <td className="px-5 py-3">
                        <p className="font-medium text-slate-900">{c.name}</p>
                        <p className="text-xs text-slate-400">{c.email}</p>
                      </td>
                      <td className="px-5 py-3">
                        {c.account_status === 'pending_activation' ? (
                          <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">⏳ Aktivierung offen</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
                            <Sparkles className="h-3 w-3" /> Pro aktiv
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-slate-700">{fmt(billing.license_price)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50/60">
                    <td className="px-5 py-3 text-xs font-medium text-slate-500" colSpan={2}>Lizenzen gesamt ({billing.license_count})</td>
                    <td className="px-5 py-3 text-right font-mono font-semibold text-slate-900" data-testid="license-total">
                      {fmt(billing.license_count * billing.license_price)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Selbstzahler-Mandanten → Lizenz-Übernahme */}
        {billing.self_paid_clients.length > 0 && (
          <>
            <h2 className="mt-10 text-lg font-semibold text-slate-900" style={H}>Selbstzahlende Mandanten</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Diese Mandanten zahlen ihr ivoliq-Abo selbst. Du kannst sie in die Kanzlei-Lizenz übernehmen —
              bei laufendem Abo greift die Übernahme automatisch zum Abo-Ende.
            </p>
            <Card className="mt-4 rounded-2xl border border-slate-100 shadow-sm" data-testid="self-paid-clients-card">
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <tbody>
                    {billing.self_paid_clients.map((c) => (
                      <tr key={c.client_id} className="border-b border-slate-50 last:border-0" data-testid={`self-paid-row-${c.client_id}`}>
                        <td className="px-5 py-3">
                          <p className="font-medium text-slate-900">{c.name}</p>
                          <p className="text-xs text-slate-400">{c.email}</p>
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500">
                          {c.has_lifetime_access ? 'Lifetime-Zugang' : c.has_active_subscription ? 'Eigenes Abo aktiv' : `Plan: ${(c.plan || 'free').toUpperCase()}`}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {c.takeover_scheduled ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700">
                                <Clock className="h-3 w-3" /> Übernahme zum Abo-Ende vorgemerkt
                              </span>
                              <button onClick={() => handleCancelTakeover(c.client_id)} className="text-[11px] text-slate-400 hover:text-rose-500 hover:underline" data-testid={`cancel-takeover-btn-${c.client_id}`}>
                                Zurückziehen
                              </button>
                            </span>
                          ) : c.takeover_eligible ? (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setTakeoverClient(c)}
                              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs"
                              data-testid={`takeover-btn-${c.client_id}`}
                            >
                              <UserCheck className="mr-1 h-3 w-3" />
                              In Kanzlei-Lizenz übernehmen
                            </Button>
                          ) : (
                            <span className="text-[11px] text-slate-400">Keine Lizenz nötig</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      <TierChangeDialog
        dialog={tierDialog}
        onClose={() => setTierDialog(null)}
        onConfirm={confirmTierChange}
        busy={busy}
      />
      <TakeoverConsentDialog
        client={takeoverClient}
        onClose={() => setTakeoverClient(null)}
        onConfirm={confirmTakeover}
        busy={busy}
      />
    </div>
  );
}
